import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { boolean, InferType, number, object, string } from 'yup';

import { PortfolioEngineAccount } from '@ndvr/portfolio-api';

import { RequestReason } from '@/cip/_openapi/portfolio-optimizer';
import { NumberInput } from '@/components/NumberInput';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { formatCurrency } from 'utils/number-formatter';
import { formatAccountType, titleCase } from 'utils/string-utils';
import { useSubmitInvestmentRequest } from 'views/portfolio-engine/investment-request/useSubmitInvestmentRequest';

const formSchema = object({
    withdrawalAmount: number()
        .typeError('Withdrawal amount is required')
        .required('Withdrawal amount is required')
        .min(1, 'Amount must be at least $1'),
    description: string().default(''),
    alreadyWired: boolean().default(false),
});

type FormValues = InferType<typeof formSchema>;

interface Props {
    account: PortfolioEngineAccount;
    onClose: () => void;
}

export const UrgentWithdrawalDialog = ({ account, onClose }: Props) => {
    const { submit, isPending } = useSubmitInvestmentRequest();

    const form = useForm<FormValues>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            withdrawalAmount: undefined,
            description: '',
            alreadyWired: false,
        },
        mode: 'onSubmit',
    });

    const subParts = [
        account.custodyAccountId,
        account.custodyType ? titleCase(account.custodyType) : null,
        formatAccountType(account.accountType),
    ].filter(Boolean);

    const handleSubmit = async (data: FormValues) => {
        const outcome = await submit({
            accountGUID: account.accountGUID,
            accountGroupGUID: account.accountGroupGUID,
            householdGUID: account.householdGUID,
            reason: RequestReason.WITHDRAWAL,
            description: data.description,
            withdrawalAmount: data.withdrawalAmount,
            alreadyWired: data.alreadyWired,
        });
        if (outcome !== 'failure') onClose();
    };

    return (
        <Dialog
            open
            onOpenChange={(open) => {
                if (!open && !isPending) onClose();
            }}
        >
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Urgent Withdrawal</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-0.5">
                    <span className="text-cocoa-1000 font-medium">{account.accountNamePlain ?? '—'}</span>
                    {subParts.length > 0 && <span className="text-cocoa-500 text-xs">{subParts.join(' · ')}</span>}
                    <span className="text-cocoa-700 mt-1 text-sm">Available cash: {formatCurrency(account.cash)}</span>
                </div>
                <Form {...form}>
                    <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
                        <FormField
                            control={form.control}
                            name="withdrawalAmount"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Withdrawal Amount</FormLabel>
                                    <FormControl>
                                        <NumberInput
                                            placeholder="Enter amount"
                                            thousandSeparator
                                            prefix="$"
                                            allowNegative={false}
                                            value={field.value ?? ''}
                                            onValueChange={(values) => field.onChange(values.floatValue ?? undefined)}
                                            onBlur={field.onBlur}
                                            name={field.name}
                                            getInputRef={field.ref}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="alreadyWired"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-center space-y-0 space-x-2">
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormLabel className="font-normal">
                                        Withdrawal already wired from cash/margin
                                    </FormLabel>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Description</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            placeholder='Optional — appended after "Urgent withdrawal - $X"'
                                            rows={4}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <p className="text-muted-foreground text-sm">
                            A withdrawal for the next business day will be created automatically.
                        </p>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isPending}>
                                {isPending ? 'Submitting…' : 'Submit'}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};
